/**
 * SEOHead Component - Dynamic meta tags, OG, Twitter, structured data, canonical, hreflang
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEOHead = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noIndex = false,
  structuredData = null,
  canonical = null,
  children,
}) => {
  const { t, i18n } = useTranslation();

  const siteName = 'VowsEra.com';
  const defaultDescription = 'America\'s #1 wedding planning platform. Find 5000+ verified vendors, create wedding websites, manage guests, budget & more.';
  const defaultImage = `${window.location.origin}/og-image.jpg`;
  const currentUrl = url || window.location.href;
  const canonicalUrl = canonical || currentUrl.split('?')[0];

  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} — Love. Planned Beautifully.`;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;

  const HREFLANG_MAP = { en: 'en', hi: 'hi', ta: 'ta', gu: 'gu', fr: 'fr', es: 'es' };

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang for i18n */}
      {Object.entries(HREFLANG_MAP).map(([code, hreflang]) => (
        <link key={code} rel="alternate" hrefLang={hreflang} href={`${canonicalUrl}?lang=${code}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={i18n.language === 'hi' ? 'hi_IN' : i18n.language === 'ta' ? 'ta_IN' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Mobile */}
      <meta name="theme-color" content="#9B30FF" />
      <meta name="apple-mobile-web-app-title" content="VowsEra" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}

      {children}
    </Helmet>
  );
};

export default SEOHead;
